import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import logger from '../utils/logger';

const EMAIL_TEMPLATES = {
  notification: {
    label: 'Notification',
    fields: [
      { key: 'title', label: 'Title', required: true },
      { key: 'message', label: 'Message', required: true, multiline: true, rows: 4 },
      { key: 'subject', label: 'Subject' },
      { key: 'greeting', label: 'Greeting' },
      { key: 'details', label: 'Details', multiline: true, rows: 3 },
      { key: 'action_url', label: 'Action URL' },
      { key: 'action_text', label: 'Action Button Text' },
      { key: 'additional_info', label: 'Additional Info', multiline: true, rows: 2 },
      { key: 'company_name', label: 'Company Name' },
    ],
  },
  alert: {
    label: 'Alert',
    fields: [
      { key: 'alert_type', label: 'Alert Type', required: true },
      { key: 'subject', label: 'Subject', required: true },
      { key: 'message', label: 'Message', required: true, multiline: true, rows: 4 },
      {
        key: 'severity',
        label: 'Severity',
        type: 'select',
        options: ['critical', 'warning', 'info', 'success'],
        defaultValue: 'critical',
      },
      { key: 'system', label: 'System' },
      { key: 'details', label: 'Details', multiline: true, rows: 3 },
      { key: 'impact', label: 'Impact', multiline: true, rows: 2 },
      { key: 'resolution_steps', label: 'Resolution Steps', multiline: true, rows: 3, list: true },
      { key: 'action_url', label: 'Action URL' },
      { key: 'action_text', label: 'Action Button Text' },
      { key: 'contact_info', label: 'Contact Info' },
      { key: 'company_name', label: 'Company Name' },
    ],
  },
  report: {
    label: 'Report',
    fields: [
      { key: 'report_type', label: 'Report Type', required: true },
      { key: 'subject', label: 'Subject', required: true },
      { key: 'summary', label: 'Summary', multiline: true, rows: 3 },
      { key: 'period', label: 'Period' },
      { key: 'recommendations', label: 'Recommendations', multiline: true, rows: 3, list: true },
      { key: 'action_url', label: 'Action URL' },
      { key: 'action_text', label: 'Action Button Text' },
      { key: 'notes', label: 'Notes', multiline: true, rows: 2 },
      { key: 'company_name', label: 'Company Name' },
      { key: 'next_report_date', label: 'Next Report Date' },
    ],
  },
  changelog: {
    label: 'Changelog',
    fields: [
      { key: 'product_name', label: 'Product Name', required: true },
      { key: 'version', label: 'Version', required: true },
      { key: 'release_date', label: 'Release Date' },
      {
        key: 'release_type',
        label: 'Release Type',
        type: 'select',
        options: ['major', 'minor', 'patch', 'hotfix'],
      },
      { key: 'intro_message', label: 'Intro Message', multiline: true, rows: 2 },
      { key: 'new_features', label: 'New Features', multiline: true, rows: 3, list: true },
      { key: 'improvements', label: 'Improvements', multiline: true, rows: 3, list: true },
      { key: 'bug_fixes', label: 'Bug Fixes', multiline: true, rows: 3, list: true },
      { key: 'breaking_changes', label: 'Breaking Changes', multiline: true, rows: 2, list: true },
      { key: 'action_url', label: 'Action URL' },
      { key: 'action_text', label: 'Action Button Text' },
      { key: 'documentation_url', label: 'Documentation URL' },
      { key: 'contact_email', label: 'Contact Email' },
    ],
  },
  reengagement: {
    label: 'Re-engagement (30-day inactive)',
    fields: [
      { key: 'subject', label: 'Subject', defaultValue: 'We miss you at Salinator' },
      { key: 'headline', label: 'Headline', defaultValue: 'Come back to Salinator' },
      { key: 'greeting', label: 'Greeting', defaultValue: 'Hello' },
      {
        key: 'message',
        label: 'Message',
        multiline: true,
        rows: 4,
        defaultValue:
          'We noticed you have not signed in to Salinator recently. Your outreach data, claims, and research are still here whenever you are ready to pick up where you left off.',
      },
      { key: 'days_inactive', label: 'Days Inactive', defaultValue: '30' },
      {
        key: 'highlights',
        label: 'Highlights',
        multiline: true,
        rows: 4,
        list: true,
        defaultValue:
          'Your claimed records and outreach history are saved and ready\nResearch tools and DNS insights help you prioritize the right accounts\nPick up your workflow in minutes — no setup required',
      },
      { key: 'action_url', label: 'Action URL', required: true },
      { key: 'action_text', label: 'Action Button Text', defaultValue: 'Open Salinator' },
      {
        key: 'additional_info',
        label: 'Text Above Optional Link',
        multiline: true,
        rows: 2,
        defaultValue: 'If you need help getting started again, schedule a Q & A:',
      },
      { key: 'secondary_action_url', label: 'Optional Link URL (e.g. booking page)' },
      {
        key: 'secondary_action_text',
        label: 'Optional Link Button Text',
        defaultValue: 'Schedule Q & A',
      },
      { key: 'company_name', label: 'Company Name' },
    ],
  },
};

const buildTemplateData = (templateType, formValues) => {
  const template = EMAIL_TEMPLATES[templateType];
  const data = {};

  template.fields.forEach((field) => {
    const rawValue = formValues[field.key] ?? field.defaultValue;
    if (rawValue === undefined || rawValue === null || String(rawValue).trim() === '') {
      return;
    }

    if (field.key === 'days_inactive') {
      const days = parseInt(String(rawValue).trim(), 10);
      if (!Number.isNaN(days)) {
        data[field.key] = days;
      }
      return;
    }

    if (field.list) {
      const items = String(rawValue)
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);
      if (items.length > 0) {
        data[field.key] = items;
      }
      return;
    }

    data[field.key] = String(rawValue).trim();
  });

  return data;
};

const getDefaultFormValues = (templateType, frontendUrl = '', bookingUrl = '') => {
  const values = formValuesFromPreset(templateType, {});
  if (templateType === 'reengagement') {
    if (frontendUrl) {
      values.action_url = frontendUrl;
    }
    if (bookingUrl) {
      values.secondary_action_url = bookingUrl;
    }
  }
  return values;
};

const formValuesFromPreset = (templateType, templateData = {}) => {
  const values = {};
  const fields = EMAIL_TEMPLATES[templateType]?.fields || [];

  fields.forEach((field) => {
    const value = templateData[field.key];
    if (value === undefined || value === null) {
      values[field.key] = field.defaultValue || '';
      return;
    }

    if (field.list && Array.isArray(value)) {
      values[field.key] = value.join('\n');
      return;
    }

    values[field.key] = String(value);
  });

  return values;
};

const EmailAdminTab = () => {
  const { user } = useAuth();
  const [templateType, setTemplateType] = useState('notification');
  const [senderEmail, setSenderEmail] = useState('');
  const [formValues, setFormValues] = useState({});
  const [recipients, setRecipients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [presetShared, setPresetShared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [serviceStatus, setServiceStatus] = useState({
    loading: true,
    configured: false,
    enabled: false,
    defaultSender: null,
    frontendUrl: '',
    bookingUrl: '',
  });
  const [inactiveLoading, setInactiveLoading] = useState(false);

  const templateConfig = EMAIL_TEMPLATES[templateType];

  const loadServiceStatus = useCallback(async () => {
    setServiceStatus((prev) => ({ ...prev, loading: true }));
    try {
      const result = await api.adminEmailGetAuthStatus();
      if (result.success) {
        setServiceStatus({
          loading: false,
          configured: Boolean(result.configured),
          enabled: Boolean(result.enabled),
          defaultSender: result.default_sender || null,
          frontendUrl: result.frontend_url || window.location.origin,
          bookingUrl: result.booking_url || '',
        });
        if (result.default_sender) {
          setSenderEmail(result.default_sender);
        }
      } else {
        setServiceStatus({ loading: false, configured: false, enabled: false, defaultSender: null });
      }
    } catch (err) {
      logger.error('Failed to load email service status:', err);
      setServiceStatus({ loading: false, configured: false, enabled: false, defaultSender: null });
    }
  }, []);

  useEffect(() => {
    loadServiceStatus();
  }, [loadServiceStatus]);

  useEffect(() => {
    if (user?.email && !senderEmail && !serviceStatus.defaultSender) {
      setSenderEmail(user.email);
    }
  }, [user, senderEmail, serviceStatus.defaultSender]);

  const loadPresets = useCallback(async () => {
    setPresetsLoading(true);
    try {
      const result = await api.adminEmailGetPresets();
      if (result.success) {
        setPresets(result.presets || []);
      }
    } catch (err) {
      logger.error('Failed to load email presets:', err);
    } finally {
      setPresetsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await api.adminEmailSearchUsers(searchQuery.trim());
        if (result.success) {
          const selectedEmails = new Set(recipients.map((r) => r.email));
          setSearchResults((result.users || []).filter((u) => !selectedEmails.has(u.email)));
        }
      } catch (err) {
        logger.error('User search failed:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, recipients]);

  const handleFieldChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleTemplateChange = (newType) => {
    setTemplateType(newType);
    setFormValues(getDefaultFormValues(
      newType,
      serviceStatus.frontendUrl || window.location.origin,
      serviceStatus.bookingUrl || ''
    ));
    setError(null);
    setSuccess(null);
  };

  const handleLoadInactiveUsers = async () => {
    const days = parseInt(String(formValues.days_inactive ?? '30').trim(), 10) || 30;
    setInactiveLoading(true);
    setError(null);
    try {
      const result = await api.adminEmailGetInactiveUsers(days);
      if (!result.success) {
        setError(result.error || 'Failed to load inactive users');
        return;
      }

      const users = result.users || [];
      if (users.length === 0) {
        setSuccess(`No users found inactive for ${days}+ days`);
        return;
      }

      setRecipients((prev) => {
        const existingEmails = new Set(prev.map((r) => r.email));
        const merged = [...prev];
        users.forEach((user) => {
          if (user.email && !existingEmails.has(user.email)) {
            merged.push(user);
            existingEmails.add(user.email);
          }
        });
        return merged;
      });
      setSuccess(`Added ${users.length} inactive user${users.length !== 1 ? 's' : ''} (${days}+ days)`);
    } catch (err) {
      logger.error('Failed to load inactive users:', err);
      setError(err.response?.data?.error || 'Failed to load inactive users');
    } finally {
      setInactiveLoading(false);
    }
  };

  const handleAddRecipient = (userOption) => {
    if (!userOption?.email) return;
    if (recipients.some((r) => r.email === userOption.email)) return;
    setRecipients((prev) => [...prev, userOption]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleRemoveRecipient = (email) => {
    setRecipients((prev) => prev.filter((r) => r.email !== email));
  };

  const validateForm = () => {
    if (!senderEmail.trim()) {
      return 'Sender email is required';
    }
    if (recipients.length === 0) {
      return 'Select at least one recipient';
    }

    const missing = templateConfig.fields
      .filter((field) => field.required)
      .filter((field) => !String(formValues[field.key] || '').trim())
      .map((field) => field.label);

    if (missing.length > 0) {
      return `Required fields: ${missing.join(', ')}`;
    }

    return null;
  };

  const getPayload = () => ({
    sender_email: senderEmail.trim(),
    recipients: recipients.map((r) => ({ email: r.email, id: r.id, display_name: r.display_name })),
    template_name: templateType,
    template_data: buildTemplateData(templateType, formValues),
  });

  const handlePreview = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await api.adminEmailPreview(templateType, buildTemplateData(templateType, formValues));
      if (result.success) {
        setPreviewSubject(result.subject || '');
        setPreviewBody(result.body || '');
        setPreviewOpen(true);
      } else {
        setError(result.error || 'Preview failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.adminEmailSendBulk(getPayload());
      if (result.success) {
        setSuccess(result.message || `Email sent to ${result.recipient_count} recipient(s)`);
        setConfirmSendOpen(false);
      } else {
        setError(result.error || 'Failed to send email');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      setError('Preset name is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await api.adminEmailCreatePreset({
        name: presetName.trim(),
        description: presetDescription.trim(),
        template_type: templateType,
        template_data: buildTemplateData(templateType, formValues),
        is_shared: presetShared,
      });

      if (result.success) {
        setSuccess('Preset saved');
        setSavePresetOpen(false);
        setPresetName('');
        setPresetDescription('');
        setPresetShared(false);
        await loadPresets();
      } else {
        setError(result.error || 'Failed to save preset');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save preset');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadPreset = async (preset) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.adminEmailUsePreset(preset.id);
      if (result.success && result.preset) {
        const loaded = result.preset;
        setTemplateType(loaded.template_type);
        setFormValues(formValuesFromPreset(loaded.template_type, loaded.template_data));
        setSuccess(`Loaded preset "${loaded.name}"`);
        await loadPresets();
      } else {
        setError(result.error || 'Failed to load preset');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load preset');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePreset = async (presetId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.adminEmailDeletePreset(presetId);
      if (result.success) {
        setSuccess('Preset deleted');
        await loadPresets();
      } else {
        setError(result.error || 'Failed to delete preset');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete preset');
    } finally {
      setLoading(false);
    }
  };

  const filteredPresets = useMemo(
    () => presets.filter((preset) => preset.template_type === templateType),
    [presets, templateType]
  );

  const canSend = serviceStatus.configured && serviceStatus.enabled;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <EmailIcon color="primary" />
        <Typography variant="h6">Email Management</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!serviceStatus.loading && !serviceStatus.configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Email service is not configured. Add AZURE_CLIENT_ID, AZURE_CLIENT_SECRET, and AZURE_TENANT_ID
          to backend/.env (use the dedicated Graph app from Apix, not the MICROSOFT_* login credentials).
        </Alert>
      )}

      {!serviceStatus.loading && serviceStatus.configured && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Autonomous app-only sending via Microsoft Graph. Default sender: {serviceStatus.defaultSender || 'not set'}.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sender Email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  helperText="Licensed mailbox the Graph app can send as (e.g. noreply@compatible.nl)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Template</InputLabel>
                  <Select
                    value={templateType}
                    label="Template"
                    onChange={(e) => handleTemplateChange(e.target.value)}
                  >
                    {Object.entries(EMAIL_TEMPLATES).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        {config.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 280 }}>
                    <Autocomplete
                      options={searchResults}
                      loading={searchLoading}
                      inputValue={searchQuery}
                      onInputChange={(_, value) => setSearchQuery(value)}
                      onChange={(_, value) => {
                        if (value) handleAddRecipient(value);
                      }}
                      getOptionLabel={(option) =>
                        option.display_name ? `${option.display_name} (${option.email})` : option.email || ''
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search recipients"
                          placeholder="Search by name or email..."
                          helperText="Type at least 2 characters to search users"
                        />
                      )}
                      noOptionsText={searchQuery.length < 2 ? 'Type to search users' : 'No users found'}
                    />
                  </Box>
                  {templateType === 'reengagement' && (
                    <Button
                      variant="outlined"
                      onClick={handleLoadInactiveUsers}
                      disabled={inactiveLoading}
                      startIcon={inactiveLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
                      sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                    >
                      Load inactive users
                    </Button>
                  )}
                </Box>
              </Grid>

              {recipients.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {recipients.map((recipient) => (
                      <Chip
                        key={recipient.email}
                        label={recipient.display_name ? `${recipient.display_name} <${recipient.email}>` : recipient.email}
                        onDelete={() => handleRemoveRecipient(recipient.email)}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {recipients.length} recipient{recipients.length !== 1 ? 's' : ''} selected
                    {recipients.length > 1 ? ' (sent via BCC for privacy)' : ''}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {templateConfig.label} fields
                </Typography>
              </Grid>

              {templateConfig.fields.map((field) => (
                <Grid item xs={12} md={field.multiline ? 12 : 6} key={field.key}>
                  {field.type === 'select' ? (
                    <FormControl fullWidth required={field.required}>
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        value={formValues[field.key] || field.defaultValue || ''}
                        label={field.label}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      >
                        {field.options.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      required={field.required}
                      label={field.label}
                      value={formValues[field.key] || ''}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      multiline={Boolean(field.multiline)}
                      rows={field.rows || 1}
                      helperText={field.list ? 'One item per line' : undefined}
                    />
                  )}
                </Grid>
              ))}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={loading ? <CircularProgress size={18} /> : <PreviewIcon />}
                    onClick={handlePreview}
                    disabled={loading}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={() => setSavePresetOpen(true)}
                    disabled={loading}
                  >
                    Save Preset
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                    onClick={() => setConfirmSendOpen(true)}
                    disabled={loading || !canSend}
                  >
                    Send Email
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1">Saved Presets</Typography>
              <Tooltip title="Refresh presets">
                <IconButton size="small" onClick={loadPresets} disabled={presetsLoading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Showing {templateConfig.label} presets
            </Typography>

            {presetsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : filteredPresets.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No presets for this template yet.
              </Typography>
            ) : (
              <List dense disablePadding>
                {filteredPresets.map((preset) => (
                  <React.Fragment key={preset.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemText
                        primary={preset.name}
                        secondary={
                          <>
                            {preset.description && <span>{preset.description}<br /></span>}
                            Used {preset.usage_count || 0} time{preset.usage_count === 1 ? '' : 's'}
                            {preset.is_shared ? ' · Shared' : ''}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Button size="small" onClick={() => handleLoadPreset(preset)} disabled={loading}>
                          Load
                        </Button>
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => handleDeletePreset(preset.id)}
                          disabled={loading}
                          aria-label="Delete preset"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Email Preview
          <IconButton onClick={() => setPreviewOpen(false)} aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Subject: {previewSubject}
          </Typography>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 0,
              minHeight: 300,
              overflow: 'auto',
            }}
          >
            <iframe
              title="Email preview"
              srcDoc={previewBody}
              style={{ width: '100%', minHeight: 400, border: 'none' }}
              sandbox=""
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={savePresetOpen} onClose={() => setSavePresetOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Email Preset</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Preset Name"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={presetDescription}
            onChange={(e) => setPresetDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={presetShared}
                onChange={(e) => setPresetShared(e.target.checked)}
              />
            }
            label="Share with other admins"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSavePresetOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePreset} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmSendOpen} onClose={() => setConfirmSendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Send</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Send <strong>{templateConfig.label}</strong> email to {recipients.length} recipient
            {recipients.length !== 1 ? 's' : ''}?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            From: {senderEmail}
          </Typography>
          {recipients.length > 1 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Multiple recipients will be sent via BCC to protect privacy.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSendOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSend} disabled={loading}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailAdminTab;
